<template>
  <div class="campaign-whitelabel-modal rounded overflow-hidden text-dark bg-white p-3.5">
    <div class="campaign-whitelabel-modal__header flex items-center justify-between gap-3 mb-3.5">
      <div class="flex items-center gap-1">
        <h2 class="campaign-whitelabel-modal__title font-bold mb-0">{{ $t('campaign_whitelabel.picker_title') }}</h2>
      </div>
      <button class="campaign-whitelabel-modal__close text-base" @click="close">
        <i class="icon-Close font-bold"></i>
      </button>
    </div>

    <!-- body -->
    <div class="campaign-whitelabel-modal__body mb-3.5">
      <el-radio
        v-for="app in apps"
        :key="app.id"
        v-model="appId"
        class="radio-item-label mb-1 mr-0"
        :class="{ active: appId === app.id }"
        :label="app.id"
      >
        <!-- logo is an absolute URL on the record, and null for a couple of apps: el-avatar
             falls back to its own placeholder rather than a broken image -->
        <el-avatar :size="36" :src="app.logo" shape="square" fit="cover" class="campaign-whitelabel-modal__logo app-logo--flush">
          {{ app.name.charAt(0) }}
        </el-avatar>

        <span class="campaign-whitelabel-modal__text">
          <span class="font-bold text-dark text-15px">{{ app.name }}</span>
          <span v-if="app.is_default" class="block text-muted leading-normal">
            {{ $t('campaign_whitelabel.default_hint') }}
          </span>
        </span>
      </el-radio>

      <p class="mt-2 mb-0 text-muted leading-normal">{{ $t('campaign_whitelabel.picker_note') }}</p>
    </div>
    <!-- end body -->

    <div class="campaign-whitelabel-modal__footer-action flex justify-end items-center gap-2">
      <el-button class="font-semibold" @click="close">{{ $t('cancel') }}</el-button>
      <el-button class="font-semibold ml-0" type="primary" :disabled="appId === null" @click="ctaContinue">
        Continue
      </el-button>
    </div>
  </div>
</template>

<script>
// el-avatar is only globally registered as a side effect of the campaigns table header
// being loaded (Vue.use(Avatar) there). Register it locally so this works on any page.
import { Avatar } from 'element-ui'

/**
 * PD-4861 — asked when the account can access more than one whitelabel, before the
 * campaign form is opened. Accounts with a single app never see it: the caller
 * navigates straight to the form and the server resolves app_id.
 *
 * Mirrors social-boost/modal/CampaignType: same $Modal contract, same radio-row
 * styling, and the modal itself owns the navigation on Continue.
 */
export default {
  name: 'CampaignWhitelabelModal',

  components: { ElAvatar: Avatar },

  props: {
    // Provided through $Modal.init({ propsData: { meta: { apps } } }).
    meta: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      appId: null,
    }
  },

  computed: {
    apps() {
      return this.meta?.apps || []
    },
  },

  methods: {
    close() {
      this.$Modal.instance.isOpen = false
    },

    ctaContinue() {
      this.close()
      // Carried as a query param so a refresh of the form keeps the choice.
      this.$router.push({ name: 'campaign-new', query: { app_id: String(this.appId) } })
    },
  },
}
</script>

<style lang="scss">
.campaign-whitelabel-modal {
  &__logo {
    @apply shrink-0 bg-white;
  }

  &__text {
    @apply flex flex-col;
  }

  .radio-item-label {
    @apply w-full p-3 rounded border border-[#e4e7ed];

    // el-radio is inline with a nowrap label by default, which pushed the input to the
    // bottom of a two-line row. Lay the row out as flex so the radio, the logo and the
    // text are all vertically centred against each other.
    display: flex;
    align-items: center;
    white-space: normal;

    .el-radio__label {
      @apply flex items-center gap-2 flex-1;
      padding-left: 10px;
    }

    &.active {
      @apply border-primary;
    }
  }

  // Overwrite el-radio style, same as the social-boost campaign-type modal
  .radio-item-label:hover .el-radio__inner,
  .el-radio__input.is-checked .el-radio__inner {
    @apply border-primary bg-transparent;
  }
  .el-radio__input.is-checked .el-radio__inner {
    &:after {
      @apply w-[7px] h-[7px] bg-primary;
    }
  }
}
</style>
